import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useAppStore } from "@/hooks/useAppStore";
import { useTranslation } from "@/lib/translate";
import React, { useEffect, useState } from "react";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { getSourceAudioURL } from "@/lib/utils";

type CommentTranslation = {
  code: string;
  name: string;
};

interface CommentTranslationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commentContent: { id: string, comment: string, commentType: string, email: string, expectedTranscription?: string, expectedTranslation?: string, currentTranscription?: string, translations?: Record<string, string>, targetLanguage?: string, messageAuthor?: string };
  setCommentContent: React.Dispatch<React.SetStateAction<{ id: string, comment: string, commentType: string, email: string, expectedTranscription?: string, expectedTranslation?: string, currentTranscription?: string, translations?: Record<string, string>, targetLanguage?: string, messageAuthor?: string }>>;
}

export const CommentTranslationDialog: React.FC<CommentTranslationDialogProps> = ({
  open,
  onOpenChange,
  commentContent,
  setCommentContent
}) => {
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const commentMessage = useAppStore((state) => state.commentMessage);
  const roomId = useAppStore((state) => state.roomId);
  const t = useTranslation(preferredLanguage);

  const COMMENT_TRANSLATION_TYPES: CommentTranslation[] = [
    { code: "translation", name: t("Translation") },
    { code: "transcription", name: t("Transcription") },
    { code: "performance", name: t("Performance") },
    { code: "other", name: t("Other") },
  ];
  const selectedCommentType = COMMENT_TRANSLATION_TYPES.find(type => type.code === commentContent.commentType) || COMMENT_TRANSLATION_TYPES[0];

  const [error, setError] = useState("");
  const [selected, setSelected] = useState<CommentTranslation>(selectedCommentType);

  useEffect(() => {
    if (!open) {
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");
    if (!commentContent.comment || commentContent.comment.trim() === "") {
      setError(t("Comment is required."));
      return;
    }

    commentMessage(commentContent.id,
      commentContent.comment,
      commentContent.commentType || COMMENT_TRANSLATION_TYPES[0].code,
      commentContent.email || "",
      commentContent.expectedTranscription,
      commentContent.expectedTranslation,
      commentContent.targetLanguage
    );

    onOpenChange(false);
  };

  const handleDownload = (e: React.FormEvent) => {
    e.preventDefault();
    const id = commentContent.id;
    const audioUrl = getSourceAudioURL(id, roomId || "", commentContent.messageAuthor || "");
    // Open audio file in a new tab/window
    window.open(audioUrl, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby="comment-translation-dialog"
        className="w-[95vw] sm:max-w-[425px] bg-neutral-800 border-neutral-700 text-white rounded-2xl p-4 sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            {t("Submit feedback")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
            <Label
              htmlFor="comment-type"
              className="text-left font-medium text-sm"
            >
              {t("Category")}{"\u00A0"}*
            </Label>
            <select
              id="comment-type"
              value={selected.code}
              onChange={(e) => {
                const type = COMMENT_TRANSLATION_TYPES.find(
                  (t) => t.code === e.target.value
                );
                if (type) {
                  setSelected(type);
                  setCommentContent({
                    ...commentContent,
                    commentType: type.code,
                  });
                }
              }}
              className="sm:col-span-3 w-full border border-neutral-600 px-3 py-2 text-sm text-neutral-200 bg-neutral-700 rounded-md focus:ring-2 focus:ring-chotto-gold"
            >
              <option value="" disabled>
                {t("Select Type")}
              </option>
              {COMMENT_TRANSLATION_TYPES.map((type) => (
                <option key={type.code} value={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {selected.code === "transcription" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                <Label
                  htmlFor="current-transcription"
                  className="text-left font-medium text-sm"
                >
                  {t("Current Transcription")}
                </Label>
                <Textarea
                  id="current-transcription"
                  value={commentContent.currentTranscription || ""}
                  readOnly
                  className="sm:col-span-3 bg-neutral-800 border-neutral-600 text-neutral-400 resize-y min-h-[40px] focus:ring-0 cursor-not-allowed"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                <Label
                  htmlFor="expected-transcription"
                  className="text-left font-medium text-sm"
                >
                  {t("Expected Transcription")}
                </Label>
                <Textarea
                  id="expected-transcription"
                  value={commentContent.expectedTranscription || ""}
                  onChange={(e) =>
                    setCommentContent({
                      ...commentContent,
                      expectedTranscription: e.target.value,
                    })
                  }
                  className="sm:col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-2 focus:ring-chotto-gold resize-y min-h-[40px]"
                  placeholder={t("What should it have been?")}
                />
              </div>
            </>
          )}

          {selected.code === "translation" && commentContent.translations && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
                <Label
                  htmlFor="target-language"
                  className="text-left font-medium text-sm"
                >
                  {t("Language")}
                </Label>
                <select
                  id="target-language"
                  value={commentContent.targetLanguage || ""}
                  onChange={(e) =>
                    setCommentContent({
                      ...commentContent,
                      targetLanguage: e.target.value,
                      expectedTranslation: "", // Reset expected text when changing language
                    })
                  }
                  className="sm:col-span-3 w-full border border-neutral-600 px-3 py-2 text-sm text-neutral-200 bg-neutral-700 rounded-md focus:ring-2 focus:ring-chotto-gold"
                >
                  <option value="" disabled>
                    {t("Select Language")}
                  </option>
                  {Object.keys(commentContent.translations).map((lang) => (
                    <option key={lang} value={lang}>
                      {lang.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {commentContent.targetLanguage && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                    <Label
                      htmlFor="current-translation"
                      className="text-left font-medium text-sm"
                    >
                      {t("Current Translation")}
                    </Label>
                    <Textarea
                      id="current-translation"
                      value={commentContent.translations[commentContent.targetLanguage] || ""}
                      readOnly
                      className="sm:col-span-3 bg-neutral-800 border-neutral-600 text-neutral-400 resize-y min-h-[40px] focus:ring-0 cursor-not-allowed"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                    <Label
                      htmlFor="expected-translation"
                      className="text-left font-medium text-sm"
                    >
                      {t("Expected Translation")}
                    </Label>
                    <Textarea
                      id="expected-translation"
                      value={commentContent.expectedTranslation || ""}
                      onChange={(e) =>
                        setCommentContent({
                          ...commentContent,
                          expectedTranslation: e.target.value,
                        })
                      }
                      className="sm:col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-2 focus:ring-chotto-gold resize-y min-h-[40px]"
                      placeholder={t("What should it have been?")}
                    />
                  </div>
                </>
              )}
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
            <Label
              htmlFor="comment-text"
              className="text-left font-medium text-sm"
            >
              {t("Comment")}{"\u00A0"}*
            </Label>
            <Textarea
              id="comment-text"
              value={commentContent.comment}
              onChange={(e) =>
                setCommentContent({
                  ...commentContent,
                  comment: e.target.value,
                })
              }
              className="sm:col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-2 focus:ring-chotto-gold resize-y min-h-[40px]"
              placeholder={t("Enter your comment")}
            />
          </div>



          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
            <Label
              htmlFor="comment-email"
              className="text-left font-medium text-sm"
            >
              {t("Email")}
            </Label>
            <div className="sm:col-span-3 space-y-2">
              <Input
                id="comment-email"
                value={commentContent.email}
                onChange={(e) =>
                  setCommentContent({
                    ...commentContent,
                    email: e.target.value,
                  })
                }
                className="w-full bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-2 focus:ring-chotto-gold"
                placeholder={t("Enter your email")}
              />
              <p className="text-xs text-neutral-400">
                {t(
                  "Optionally enter your email if you'd like us to get back to you!"
                )}
              </p>
            </div>
          </div>

          <hr className="border-neutral-700" />

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center">
            <Label className="text-left font-medium text-sm">
              {t("Audio file")}:
            </Label>
            <div className="sm:col-span-3 flex justify-start">
              <Button
                type="button"
                onClick={handleDownload}
                className="w-auto bg-chotto-share-blue hover:bg-chotto-share-blue/90"
              >
                {t("Play recording")}
              </Button>
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center sm:text-left">
              {error}
            </p>
          )}

          <DialogFooter className="flex flex-row gap-2 justify-end pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="w-auto"
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              className="w-auto bg-chotto-share-blue hover:bg-chotto-share-blue/90"
            >
              {t("Submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

  );
};
